import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaDownload } from "react-icons/fa";
import * as XLSX from 'xlsx';
import "../../styles/chart.css";

// Fungsi untuk mengunduh data dalam bentuk Excel
export const handleDownloadExcel = (data, filename = 'data.xlsx') => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
};

const DataChart = ({ data, onPieClick, activeFilter }) => {
  const [chartData, setChartData] = useState([]);

  // Gunakan useEffect untuk memastikan data diproses ketika berubah
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Hitung distribusi status
    const statusCounts = data.reduce((acc, item) => {
      const status = item.status || 'Tidak Ada Status';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Konversi ke format chart dan urutkan berdasarkan jumlah
    const newChartData = Object.keys(statusCounts)
      .map((status) => ({
        name: status,
        value: statusCounts[status],
        percentage: (statusCounts[status] / data.length * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);

    setChartData(newChartData);
  }, [data]);

  // Warna untuk pie chart - menambahkan lebih banyak warna
  const getColorByStatus = (status) => {
    const colorMap = {
      'Sudah Terintegrasi': '#4CAF50',     
      'Perlu Perbaikan': '#FF9800',       
      'Sedang Verifikasi': '#2196F3',     
      'Belum Diverifikasi': '#9E9E9E',     
      'Revisi Pengajuan': '#FFEB3B',    
    };
    return colorMap[status] || '#607D8B';
  };

  // Custom label function dengan positioning yang lebih smart
  const renderCustomLabel = (entry) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, name, index } = entry;

    const RADIAN = Math.PI / 180;

    // Gunakan radius yang berbeda berdasarkan ukuran slice
    let labelRadius;
    if (percent > 0.15) {
      labelRadius = outerRadius * 1.2;
    } else if (percent > 0.05) {
      labelRadius = outerRadius * 1.3;
    } else {
      labelRadius = outerRadius * 1.5;
    }

    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);

    // Untuk slice yang sangat kecil, gunakan font yang lebih kecil
    const fontSize = percent < 0.05 ? "10" : "12";

    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight="500"
      >
        {percent < 0.02 ? `${(percent * 100).toFixed(1)}%` : `${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  // Handle pie click dengan informasi yang lebih jelas
  const handlePieClick = (data, index) => {
    if (onPieClick) {
      onPieClick(data);
    }
  };

  // Jika tidak ada data atau data chart kosong
  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-container1">
        <div className="chart-header">
          <h4>Distribusi Status Data</h4>
        </div>
        <div className="chart-empty">
          <p className="text-center text-muted">Tidak ada data status untuk ditampilkan</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container1">
      <div className="chart-header">
        <h4>Distribusi Status Data</h4>
        <button
          className="btn btn-link text-success"
          onClick={() => handleDownloadExcel(data)}
          title="Unduh Data"
        >
          <FaDownload size={20} />
        </button>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={renderCustomLabel}
              outerRadius={100}
              innerRadius={0}
              fill="#8884d8"
              dataKey="value"
              onClick={handlePieClick}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
              className={activeFilter ? 'pie-active' : ''}
              minAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorByStatus(entry.name)}
                  className={activeFilter === entry.name ? 'active-cell' : ''}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value} data (${props.payload.percentage}%)`,
                'Jumlah'
              ]}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DataChart;